import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Chip,
  MenuItem,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Legend,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip as RechartsTooltip,
  XAxis,
  YAxis,
} from 'recharts';

const fmtDate = (d) => (d ? new Date(d).toLocaleDateString('fr-FR') : '—');
const fmtKm = (v) => (v != null ? `${Number(v).toFixed(2)} km` : '—');
const fmtEuro = (v) => (v != null ? `${Number(v).toFixed(2)} €` : '—');
const fmtIkCount = (v) => `${Number(v || 0)} IK`;
const isApprovedIk = (entry) => entry?.status === 'approved';

const chartPalette = ['#2563eb', '#0f766e', '#7c3aed', '#d97706', '#be185d', '#15803d', '#0891b2', '#4f46e5'];
const monthLabels = ['Jan', 'Fev', 'Mar', 'Avr', 'Mai', 'Juin', 'Juil', 'Aou', 'Sep', 'Oct', 'Nov', 'Dec'];

function toInputDateValue(date) {
  return new Date(date.getTime() - (date.getTimezoneOffset() * 60000)).toISOString().slice(0, 10);
}

function formatMonthKey(date) {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  return `${year}-${month}`;
}

function formatMonthLabel(monthKey) {
  const [year, month] = String(monthKey || '').split('-');
  const monthIndex = Number(month) - 1;
  if (!Number.isFinite(monthIndex) || monthIndex < 0 || monthIndex > 11) return monthKey;
  return `${monthLabels[monthIndex]} ${year}`;
}

function buildPeriod({ mode, selectedYear, selectedMonth, customStartDate, customEndDate }) {
  if (mode === 'year') {
    const start = new Date(selectedYear, 0, 1, 0, 0, 0, 0);
    const end = new Date(selectedYear, 11, 31, 23, 59, 59, 999);
    return { start, end };
  }

  if (mode === 'month') {
    const start = new Date(selectedYear, selectedMonth, 1, 0, 0, 0, 0);
    const end = new Date(selectedYear, selectedMonth + 1, 0, 23, 59, 59, 999);
    return { start, end };
  }

  if (!customStartDate || !customEndDate) return null;
  const start = new Date(`${customStartDate}T00:00:00`);
  const end = new Date(`${customEndDate}T23:59:59.999`);
  if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime()) || start > end) return null;
  return { start, end };
}

export default function IKReportView({ api, open = true }) {
  const now = useMemo(() => new Date(), []);
  const [mode, setMode] = useState('month');
  const [selectedYear, setSelectedYear] = useState(now.getFullYear());
  const [selectedMonth, setSelectedMonth] = useState(now.getMonth());
  const [customStartDate, setCustomStartDate] = useState(toInputDateValue(new Date(now.getFullYear(), now.getMonth(), 1)));
  const [customEndDate, setCustomEndDate] = useState(toInputDateValue(now));
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const selectedPeriod = useMemo(
    () => buildPeriod({ mode, selectedYear, selectedMonth, customStartDate, customEndDate }),
    [customEndDate, customStartDate, mode, selectedMonth, selectedYear]
  );

  const loadReport = useCallback(async () => {
    if (!open) return;
    if (!selectedPeriod) {
      setEntries([]);
      setError('Période invalide.');
      return;
    }

    setLoading(true);
    try {
      const { data } = await api.get('/api/ik/global', {
        params: {
          start: selectedPeriod.start.toISOString(),
          end: selectedPeriod.end.toISOString(),
        },
      });
      setEntries(Array.isArray(data?.data) ? data.data : []);
      setError('');
    } catch (e) {
      setEntries([]);
      setError(e?.response?.data?.error || e.message || 'Erreur');
    } finally {
      setLoading(false);
    }
  }, [api, open, selectedPeriod]);

  useEffect(() => {
    loadReport();
  }, [loadReport]);

  const approvedEntries = useMemo(
    () => entries.filter((entry) => isApprovedIk(entry)),
    [entries]
  );

  const availableYears = useMemo(() => {
    const years = new Set([now.getFullYear()]);
    entries.forEach((entry) => {
      if (!entry?.dateTrajet) return;
      years.add(new Date(entry.dateTrajet).getFullYear());
    });
    return Array.from(years).sort((a, b) => b - a);
  }, [entries, now]);

  const userSummaries = useMemo(() => {
    const summaries = new Map();

    approvedEntries.forEach((entry) => {
      const userId = entry?.User?._id || entry?.User || entry?.userId || '';
      const userName = entry?.userName || 'Utilisateur inconnu';
      const userKey = String(userId || userName);

      if (!summaries.has(userKey)) {
        summaries.set(userKey, {
          userKey,
          userName,
          ikCount: 0,
          distanceKm: 0,
          distanceAmount: 0,
          extraExpensesReimbursedAmount: 0,
        });
      }

      const current = summaries.get(userKey);
      current.ikCount += 1;
      current.distanceKm += Number(entry?.distanceKm || 0);
      current.distanceAmount += Number(entry?.amount || 0);
      current.extraExpensesReimbursedAmount += Number(entry?.extraExpensesReimbursedAmount || 0);
    });

    return Array.from(summaries.values()).sort((a, b) => b.distanceAmount - a.distanceAmount);
  }, [approvedEntries]);

  const pieChartData = useMemo(
    () => userSummaries
      .filter((summary) => summary.distanceAmount > 0)
      .map((summary, index) => ({
        name: summary.userName,
        value: Number(summary.distanceAmount.toFixed(2)),
        fill: chartPalette[index % chartPalette.length],
      })),
    [userSummaries]
  );

  const monthlyKmData = useMemo(() => {
    const rows = new Map();

    approvedEntries.forEach((entry) => {
      if (!entry?.dateTrajet) return;
      const date = new Date(entry.dateTrajet);
      const monthKey = formatMonthKey(date);

      if (!rows.has(monthKey)) {
        rows.set(monthKey, {
          monthKey,
          label: formatMonthLabel(monthKey),
          km: 0,
        });
      }

      rows.get(monthKey).km += Number(entry?.distanceKm || 0);
    });

    return Array.from(rows.values())
      .sort((a, b) => a.monthKey.localeCompare(b.monthKey))
      .map((row) => ({ ...row, km: Number(row.km.toFixed(2)) }));
  }, [approvedEntries]);

  const topUsers = useMemo(
    () => userSummaries.slice(0, 6),
    [userSummaries]
  );

  const userMonthlyKmData = useMemo(() => {
    const rows = new Map();

    approvedEntries.forEach((entry) => {
      if (!entry?.dateTrajet) return;
      const userId = entry?.User?._id || entry?.User || entry?.userId || '';
      const userName = entry?.userName || 'Utilisateur inconnu';
      const userKey = String(userId || userName);

      if (!topUsers.some((user) => user.userKey === userKey)) return;

      const date = new Date(entry.dateTrajet);
      const monthKey = formatMonthKey(date);

      if (!rows.has(monthKey)) {
        rows.set(monthKey, {
          monthKey,
          label: formatMonthLabel(monthKey),
        });
      }

      const current = rows.get(monthKey);
      current[userKey] = Number((current[userKey] || 0) + Number(entry?.distanceKm || 0));
    });

    return Array.from(rows.values())
      .sort((a, b) => a.monthKey.localeCompare(b.monthKey))
      .map((row) => {
        const next = { ...row };
        topUsers.forEach((user) => {
          next[user.userKey] = Number((next[user.userKey] || 0).toFixed(2));
        });
        return next;
      });
  }, [approvedEntries, topUsers]);

  const totalApprovedKm = useMemo(
    () => approvedEntries.reduce((sum, entry) => sum + Number(entry?.distanceKm || 0), 0),
    [approvedEntries]
  );
  const totalApprovedAmount = useMemo(
    () => approvedEntries.reduce((sum, entry) => sum + Number(entry?.amount || 0), 0),
    [approvedEntries]
  );
  const totalApprovedExpenses = useMemo(
    () => approvedEntries.reduce((sum, entry) => sum + Number(entry?.extraExpensesReimbursedAmount || 0), 0),
    [approvedEntries]
  );

  return (
    <Box>
      <Stack direction={{ xs: 'column', lg: 'row' }} justifyContent="space-between" spacing={2} mb={2}>
        <Box>
          <Typography variant="h6">Rapport IK</Typography>
          <Typography variant="caption" color="text.secondary">
            Synthèse des IK validées avec filtres temporels et graphiques.
          </Typography>
        </Box>
        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
          <Chip label={`IK validées: ${fmtIkCount(approvedEntries.length)}`} color="success" variant="outlined" />
          <Chip label={`KM: ${fmtKm(totalApprovedKm)}`} color="primary" variant="outlined" />
          <Chip label={`Déplacement: ${fmtEuro(totalApprovedAmount)}`} color="info" variant="outlined" />
          <Chip label={`Frais remboursés: ${fmtEuro(totalApprovedExpenses)}`} color="secondary" variant="outlined" />
        </Stack>
      </Stack>

      <Paper
        variant="outlined"
        sx={{
          p: 2,
          mb: 2,
          borderRadius: 3,
          borderColor: '#cfe0f5',
          background: 'linear-gradient(180deg, #f8fbff 0%, #eef5fb 100%)',
        }}
      >
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems={{ xs: 'stretch', md: 'center' }}>
          <TextField
            select
            label="Filtre"
            value={mode}
            onChange={(event) => setMode(event.target.value)}
            sx={{ minWidth: 180 }}
          >
            <MenuItem value="month">Mois</MenuItem>
            <MenuItem value="year">Année</MenuItem>
            <MenuItem value="custom">Période custom</MenuItem>
          </TextField>

          {(mode === 'month' || mode === 'year') ? (
            <TextField
              select
              label="Année"
              value={selectedYear}
              onChange={(event) => setSelectedYear(Number(event.target.value))}
              sx={{ minWidth: 160 }}
            >
              {availableYears.map((year) => (
                <MenuItem key={year} value={year}>
                  {year}
                </MenuItem>
              ))}
            </TextField>
          ) : null}

          {mode === 'month' ? (
            <TextField
              select
              label="Mois"
              value={selectedMonth}
              onChange={(event) => setSelectedMonth(Number(event.target.value))}
              sx={{ minWidth: 180 }}
            >
              {monthLabels.map((label, index) => (
                <MenuItem key={label} value={index}>
                  {label}
                </MenuItem>
              ))}
            </TextField>
          ) : null}

          {mode === 'custom' ? (
            <>
              <TextField
                label="Date début"
                type="date"
                value={customStartDate}
                onChange={(event) => setCustomStartDate(event.target.value)}
                sx={{ minWidth: 180 }}
                InputLabelProps={{ shrink: true }}
              />
              <TextField
                label="Date fin"
                type="date"
                value={customEndDate}
                onChange={(event) => setCustomEndDate(event.target.value)}
                sx={{ minWidth: 180 }}
                InputLabelProps={{ shrink: true }}
              />
            </>
          ) : null}

          <Box>
            <Typography variant="body2" color="text.secondary">
              Période appliquée
            </Typography>
            <Typography variant="body2">
              {selectedPeriod ? `${fmtDate(selectedPeriod.start)} → ${fmtDate(selectedPeriod.end)}` : 'Période invalide'}
            </Typography>
          </Box>
        </Stack>
      </Paper>

      {error ? (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      ) : null}

      <Paper variant="outlined" sx={{ mb: 2, borderRadius: 3, overflow: 'hidden' }}>
        <Box sx={{ px: 2, py: 1.5, bgcolor: '#f8fbff', borderBottom: '1px solid', borderColor: '#e6edf5' }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
            Totaux validés par utilisateur
          </Typography>
        </Box>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Utilisateur</TableCell>
                <TableCell align="right">IK validées</TableCell>
                <TableCell align="right">Total KM</TableCell>
                <TableCell align="right">Déplacement</TableCell>
                <TableCell align="right">Frais remboursés</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {userSummaries.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5}>
                    <Typography variant="body2" color="text.secondary">
                      {loading ? 'Chargement…' : 'Aucune IK validée sur cette période.'}
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                userSummaries.map((summary) => {
                  const isOverLimit = summary.distanceAmount > 300;

                  return (
                    <TableRow
                      key={summary.userKey}
                      sx={{
                        bgcolor: isOverLimit ? '#fff1f2' : 'inherit',
                        '& td': {
                          borderColor: isOverLimit ? '#fecdd3' : undefined,
                        },
                      }}
                    >
                      <TableCell sx={{ fontWeight: 700 }}>{summary.userName}</TableCell>
                      <TableCell align="right">{summary.ikCount}</TableCell>
                      <TableCell align="right">{fmtKm(summary.distanceKm)}</TableCell>
                      <TableCell align="right">
                        <Typography
                          variant="body2"
                          sx={{ fontWeight: 700, color: isOverLimit ? 'error.main' : 'text.primary' }}
                        >
                          {fmtEuro(summary.distanceAmount)}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">{summary.extraExpensesReimbursedAmount > 0 ? fmtEuro(summary.extraExpensesReimbursedAmount) : '—'}</TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', xl: '1fr 1fr' },
          gap: 2,
        }}
      >
        <Paper variant="outlined" sx={{ p: 2, borderRadius: 3, minHeight: 340 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1.5 }}>
            Répartition des déplacements (%) par utilisateur
          </Typography>
          {pieChartData.length === 0 ? (
            <Box sx={{ pt: 8, textAlign: 'center' }}>
              <Typography variant="body2" color="text.secondary">
                Aucune donnée à afficher.
              </Typography>
            </Box>
          ) : (
            <ResponsiveContainer width="100%" height={280}>
              <PieChart>
                <Pie data={pieChartData} dataKey="value" nameKey="name" innerRadius={60} outerRadius={95} labelLine={false}>
                  {pieChartData.map((entry) => (
                    <Cell key={entry.name} fill={entry.fill} />
                  ))}
                </Pie>
                <RechartsTooltip formatter={(value) => [fmtEuro(value), 'Déplacement']} />
                <Legend iconType="circle" />
              </PieChart>
            </ResponsiveContainer>
          )}
        </Paper>

        <Paper variant="outlined" sx={{ p: 2, borderRadius: 3, minHeight: 340 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1.5 }}>
            KM globaux par mois
          </Typography>
          {monthlyKmData.length === 0 ? (
            <Box sx={{ pt: 8, textAlign: 'center' }}>
              <Typography variant="body2" color="text.secondary">
                Aucune donnée à afficher.
              </Typography>
            </Box>
          ) : (
            <ResponsiveContainer width="100%" height={280}>
              <BarChart data={monthlyKmData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="label" />
                <YAxis />
                <RechartsTooltip formatter={(value) => [fmtKm(value), 'KM']} />
                <Bar dataKey="km" name="KM" fill="#2563eb" radius={[6, 6, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          )}
        </Paper>
      </Box>

      <Paper variant="outlined" sx={{ p: 2, borderRadius: 3, mt: 2, minHeight: 360 }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1.5 }}>
          KM par utilisateur et par mois
        </Typography>
        {userMonthlyKmData.length === 0 || topUsers.length === 0 ? (
          <Box sx={{ pt: 8, textAlign: 'center' }}>
            <Typography variant="body2" color="text.secondary">
              Aucune donnée à afficher.
            </Typography>
          </Box>
        ) : (
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={userMonthlyKmData}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="label" />
              <YAxis />
              <RechartsTooltip formatter={(value) => [fmtKm(value), 'KM']} />
              <Legend />
              {topUsers.map((user, index) => (
                <Bar
                  key={user.userKey}
                  dataKey={user.userKey}
                  name={user.userName}
                  fill={chartPalette[index % chartPalette.length]}
                  radius={[4, 4, 0, 0]}
                />
              ))}
            </BarChart>
          </ResponsiveContainer>
        )}
      </Paper>
    </Box>
  );
}
